import { useAppContext } from "AppContext";
import { ModalMode } from "apps/events/types/ModalMode";
import { format } from "date-fns";
import {
  EnumOwnerType,
  EnumEventAccessOperatorStatus,
  EventByIdQuery,
  EventManyAppQuery,
  useEventManyAppQuery,
} from "gql/generated";
import { useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useLoggedInAs } from "shared/hooks/useLoggedInAs";
import {
  EVENT_DATE_FORMAT,
  MAX_DATE,
  MIN_EVENT_DATE,
} from "shared/utils/eventHelpers";

export const useOperatorEvents = () => {
  // Get the logged in user info
  const { dbUser, operators } = useAppContext();
  const loggedInAs = useLoggedInAs();
  const { operatorEntityId } = useParams();
  const limitHostToOperatorEntityId =
    loggedInAs?.type === EnumOwnerType.Operator ? operatorEntityId : undefined;

  // Determine the default operator IDs to use for the create event modal
  const defaultOperatorEntityIds = useMemo(() => {
    if (loggedInAs?.type === EnumOwnerType.Operator) {
      return operators.map((operator) => operator._entityId);
    }

    return [];
  }, [loggedInAs, operators]);

  // Determine the default organization to use for the create event modal
  const defaultOrganizationId = useMemo(() => {
    if (loggedInAs?.type === EnumOwnerType.Organization) {
      return loggedInAs.entityId;
    } else if (loggedInAs?.type === EnumOwnerType.Operator) {
      const operatorEntityId = loggedInAs?.entityId;
      const operator = operators.find(
        (op) => op._entityId === operatorEntityId,
      );
      return operator?.enrollments[0]?.organization ?? "";
    }

    return "";
  }, [loggedInAs, operators]);

  // Track the state of the create event modal
  const [isCreateEditModalOpen, setIsCreateEditModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string>("");
  const [mode, setMode] = useState<ModalMode>("create");
  const [showPastEvents, setShowPastEvents] = useState(false);

  // When creating a new event, set the mode to create and open the modal
  const handleCreateNewEvent = () => {
    setIsCreateEditModalOpen(true);
    setMode("create");
  };

  // When editing an existing event, set the mode to edit and open the modal
  const handleEditEvent = (eventId: string, editMode: ModalMode) => {
    setIsCreateEditModalOpen(true);
    setSelectedEventId(eventId);
    setMode(editMode);
  };

  // When closing the modal, reset the mode and selected event ID
  const handleCloseCreateEditModal = () => {
    setIsCreateEditModalOpen(false);
    setMode("create");
    setSelectedEventId("");
  };

  /**
   * Update or add an event
   * - This allows us to skip refetching the events array when an event is modified in the modal
   * @param event
   */
  const handleUpdateOrAddEvent = (event: EventByIdQuery["eventById"]) => {
    if (!event) return;

    const events = [...allEvents.current];
    const existingEventIndex = allEvents.current.findIndex(
      (e) => e._id === event._id,
    );
    if (existingEventIndex !== -1) {
      events[existingEventIndex] = event as EventManyAppQuery["eventMany"][0];
    } else {
      events.push(event as EventManyAppQuery["eventMany"][0]);
    }
    allEvents.current = events;

    groupEventsByStatus();
  };

  /**
   * Remove an event from the events array
   * @param eventId
   */
  const handleRemoveEvent = (eventId: string) => {
    if (!eventId) return;

    const events = [...allEvents.current];
    const existingEventIndex = allEvents.current.findIndex(
      (e) => e._id === eventId,
    );
    if (existingEventIndex !== -1) {
      events.splice(existingEventIndex, 1);
    }
    allEvents.current = events;

    groupEventsByStatus();
  };

  const allEvents = useRef<EventManyAppQuery["eventMany"]>([]);
  const [cancelledEvents, setCancelledEvents] = useState<
    EventManyAppQuery["eventMany"]
  >([]);
  const [declinedEvents, setDeclinedEvents] = useState<
    EventManyAppQuery["eventMany"]
  >([]);
  const [draftEvents, setDraftEvents] = useState<
    EventManyAppQuery["eventMany"]
  >([]);
  const [inReviewEvents, setInReviewEvents] = useState<
    EventManyAppQuery["eventMany"]
  >([]);
  const [approvedEvents, setApprovedEvents] = useState<
    EventManyAppQuery["eventMany"]
  >([]);
  const [pastEvents, setPastEvents] = useState<EventManyAppQuery["eventMany"]>(
    [],
  );
  const { loading: loadingEvents, error: errorEvents } = useEventManyAppQuery({
    variables: {
      startDate: MIN_EVENT_DATE,
      endDate: MAX_DATE,
      operatorId: operatorEntityId,
      keepEventsWithNoFutureOccurrences: true,
    },
    onCompleted: (data) => {
      allEvents.current = data.eventMany;
      groupEventsByStatus();
    },
  });

  /**
   * Handle grouping events by their status and endDate
   */
  const groupEventsByStatus = () => {
    const now = format(new Date(), EVENT_DATE_FORMAT);

    const endDate = (event: EventManyAppQuery["eventMany"][0]) =>
      event.recurrencePattern?.endDate || event.endDate || event.startDate;

    setCancelledEvents(
      allEvents.current.filter(
        (event) =>
          endDate(event) >= now &&
          event.access.organizations.find(
            (organization) => organization?._entityId === defaultOrganizationId,
          )?.status === EnumEventAccessOperatorStatus.Cancelled,
      ),
    );
    setDeclinedEvents(
      allEvents.current.filter(
        (event) =>
          endDate(event) >= now &&
          event.access.organizations.find(
            (organization) => organization?._entityId === defaultOrganizationId,
          )?.status === EnumEventAccessOperatorStatus.Declined,
      ),
    );
    setDraftEvents(
      allEvents.current.filter(
        (event) =>
          endDate(event) >= now &&
          event.access.organizations.find(
            (organization) => organization?._entityId === defaultOrganizationId,
          )?.status === EnumEventAccessOperatorStatus.Draft,
      ),
    );
    setInReviewEvents(
      allEvents.current.filter(
        (event) =>
          endDate(event) >= now &&
          event.access.organizations.find(
            (organization) => organization?._entityId === defaultOrganizationId,
          )?.status === EnumEventAccessOperatorStatus.ForReview,
      ),
    );
    setApprovedEvents(
      allEvents.current.filter(
        (event) =>
          endDate(event) >= now &&
          event.access.organizations.find(
            (organization) => organization?._entityId === defaultOrganizationId,
          )?.status === EnumEventAccessOperatorStatus.Approved,
      ),
    );
    setPastEvents(allEvents.current.filter((event) => endDate(event) < now));
  };

  const handlePastEventsToggle = () => {
    setShowPastEvents((prev) => !prev);
    groupEventsByStatus();
  };

  return {
    email: dbUser?.email ?? "",
    isCreateEditModalOpen,
    mode,
    selectedEventId,
    defaultOrganizationId,
    defaultOperatorEntityIds,
    limitHostToOperatorEntityId,
    userType: loggedInAs?.type ?? "PUBLIC",
    loadingEvents,
    errorEvents,
    cancelledEvents,
    declinedEvents,
    draftEvents,
    inReviewEvents,
    approvedEvents,
    pastEvents,
    showPastEvents,
    handleCreateNewEvent,
    handleEditEvent,
    handleCloseCreateEditModal,
    handleUpdateOrAddEvent,
    handleRemoveEvent,
    handlePastEventsToggle,
  };
};
